import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { FileText, Camera } from 'lucide-react';
import type { ReactNode } from 'react';

// ── Case-tab index (2026-08-15, "fixing not postponing" ruling) ─────────────
// Documentation and Photos & Evidence already work PER CASE inside the case
// workspace (EvidenceTab is fully functional against the private backend).
// These rail items were marked "soon" only because nothing routed to them.
// This index lists today's cases and deep-links straight into the relevant
// workspace tab (?tab=documentation / ?tab=evidence).

type TabKind = 'documentation' | 'evidence';

interface OrCaseRow {
    case_id: number;
    patient_name: string | null;
    procedure_name: string | null;
    or_room: string | null;
    stage: string | null;
}

const CFG: Record<TabKind, { title: string; subtitle: string; icon: ReactNode; empty: string }> = {
    documentation: {
        title: 'Documentation',
        subtitle: "Per-case perioperative documentation — pick today's case to open its record",
        icon: <FileText className="w-5 h-5" />,
        empty: 'No cases today. Documentation opens from any scheduled case.',
    },
    evidence: {
        title: 'Photos & Evidence',
        subtitle: "MFA-gated clinical evidence capture and review — pick today's case",
        icon: <Camera className="w-5 h-5" />,
        empty: 'No cases today. Evidence capture opens from any scheduled case.',
    },
};

export function CaseTabIndexRoute({ kind }: { kind: TabKind }) {
    const cfg = CFG[kind];
    const today = new Date().toLocaleDateString('en-CA'); // LOCAL calendar day

    const { data: cases = [], isLoading, error } = useQuery({
        queryKey: ['case-tab-index', today],
        queryFn: async (): Promise<OrCaseRow[]> => {
            const { data, error } = await supabase
                .from('or_cases')
                .select('case_id, patient_name, procedure_name, or_room, stage')
                .eq('case_date', today)
                .is('deleted_at', null)
                .order('scheduled_start', { ascending: true });
            if (error) throw error;
            return (data ?? []) as OrCaseRow[];
        },
    });

    return (
        <div className="min-h-full bg-pt-darker">
            <header className="border-b border-pt-border/60 px-6 py-4 flex items-center gap-3">
                <span className="text-pt-cyan [&>svg]:w-5 [&>svg]:h-5">{cfg.icon}</span>
                <div>
                    <h1 className="text-lg font-display font-semibold text-pt-text">{cfg.title}</h1>
                    <p className="text-xs text-pt-text-muted">{cfg.subtitle}</p>
                </div>
            </header>
            <div className="p-6">
                {isLoading && <div className="text-pt-text-muted text-sm">Loading…</div>}
                {error != null && (
                    <div className="text-sm text-red-400">{error instanceof Error ? error.message : 'Failed to load cases'}</div>
                )}
                {!isLoading && error == null && cases.length === 0 && (
                    <div className="border border-pt-border rounded-lg bg-pt-panel px-6 py-10 text-center text-sm text-pt-text-muted">
                        {cfg.empty}
                    </div>
                )}
                {cases.length > 0 && (
                    <ul className="space-y-2">
                        {cases.map((c) => (
                            <li key={c.case_id}>
                                <Link
                                    to={`/cases/${c.case_id}?tab=${kind}`}
                                    className="flex items-center gap-4 border border-pt-border rounded-lg bg-pt-panel px-4 py-3 hover:border-pt-cyan/50 transition-colors"
                                >
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-medium text-pt-text truncate">{c.patient_name ?? `Case #${c.case_id}`}</div>
                                        <div className="text-xs text-pt-text-muted truncate">{c.procedure_name ?? 'Procedure TBD'} · {c.or_room ?? '—'}</div>
                                    </div>
                                    <span className="uppercase tracking-wide text-[10px] text-pt-text-muted border border-pt-input-border rounded px-1.5 py-0.5 shrink-0">
                                        {c.stage ?? '—'}
                                    </span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
